import React, { memo, useState, useEffect } from 'react';
import { getLocale } from 'umi';
import { Card, Timeline, Popover, Bubble } from 'tea-component';
import moment from 'moment';
import { useIntl } from 'umi';
import DateSchedule from './DateSchedule';
import PopoverDate from './PopoverDate';
import { fetchHolidaysRegionCountries } from '@/pages/home/services/holidays';
import {
  handleRegions,
  getRegionsForRegionSelector,
} from '@/pages/home/utils/utils';
import { getWeekLocaleText } from '@/pages/home/utils/date';
import { getLocalText } from '@/pages/home/utils/lang';
import {
  IRegionType,
  IRegionValue,
  IRegion,
  IRegionOfRegionSelector,
} from '@/pages/home/types/common';
import {
  IPopoverDateProps,
  IHolidaysFetcherRes,
} from '@/pages/home/types/holidays';
import { fetchHolidays } from '@/pages/home/services/holidays';
import '@/pages/home/assets/scss/page/home/components/upcomingHoliday.scss';
import RegionSelector from '@/pages/home/pages/home/components/RegionSelector';
import Abnormal from '@/pages/home/components/Abnormal';

export default function UpcomingHoliday(props: any) {
  const today = moment();
  const locale = getLocale();
  const { formatMessage } = useIntl(); // 中英切换方法

  // 日历弹窗visible状态
  const [calendarVisible, setCalendarVisible] = useState(false);

  const [regionType, setRegionType] = useState<IRegionType>('market');
  const [regionValue, setRegionValue] = useState<IRegionValue>([]);
  const [regionCountries, setRegionCountries] = useState<
    IRegionOfRegionSelector[]
  >([]);
  const [holidaysData, setHolidaysData] = useState<IHolidaysFetcherRes>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const getWeekText = (date: string): string => {
    const m = moment(date);
    return !m.isValid() ? '' : getWeekLocaleText(formatMessage, m.day());
  };

  const getIsToday = (date: string): boolean =>
    today.format('YYYY-MM-DD') === date;

  const transGlobalValue = (regionValue: string[]) => {
    let regionValueTemp: string[] = [];
    if (Array.isArray(regionValue)) {
      regionValueTemp = regionValue[0] === '255' ? [] : regionValue;
    }
    return regionValueTemp;
  };

  const getHolidays = async (regionValue: string[]) => {
    try {
      const holidays = await fetchHolidays({
        markets: transGlobalValue(regionValue),
        start_date: today.format('YYYY-MM-DD'),
        end_date: today.add(1, 'months').format('YYYY-MM-DD'),
      });
      setHolidaysData(holidays);
      setError(false);
    } catch (err) {
      setHolidaysData([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // 获取国家地区列表数据
  useEffect(() => {
    (async () => {
      try {
        let regionCountries: IRegion[] | IRegionOfRegionSelector[] =
          await fetchHolidaysRegionCountries({
            start_date: '',
            end_date: '',
          });
        regionCountries = getRegionsForRegionSelector(
          handleRegions(regionCountries),
        );
        setRegionCountries(regionCountries);
      } catch (exception) {
      }
    })();
    getHolidays([]);
  }, []);

  return (
    <>
      <Card className="upcom-card dbh-upcoming-card">
        <Card.Body
          title={
            <div className="home-table-title">
              {formatMessage({ id: 'dbh_global_holidays' })}
            </div>
          }
          operation={
            // <></>
            <span
              className="home-table-more"
              onClick={(e) => {
                // 这里一定要阻止事件冒泡，否则无法打开日历弹窗，具体原因是由于在DateSchedule组件内通过useClickoutside hook，用事件冒泡机制监听了body的鼠标事件，在鼠标事件回调内setCalendarVisible(false)了
                e?.stopPropagation();
                setCalendarVisible(true);
              }}
            >
              {formatMessage({ id: 'dbh_more' })}
            </span>
          }
        >
          <RegionSelector
            // matchButtonWidth
            // key={FORM_MAP.REGION}
            className="dbh-upcoming-holidays-region-selector"
            value={{
              markets: regionValue, //['255'],//255=global
              region_type: 'market',
              regions: [],
            }}
            onChange={(value) => {
              setRegionType('market');
              setRegionValue(value?.markets);
              getHolidays(value?.markets);
            }}
            hiddenNoChina={false}
            locale={locale}
            globalMessageHidden={false}
            ifDiyPosition={true}
            // globalMsg={getLocalText('region_selector_global_message', {}, false)}
            regionCountry={regionCountries}
            showDismissIcon={false}
          />
          <Abnormal
            isLoading={loading}
            isError={error}
            isNoData={!holidaysData?.length}
            // style={{ height: 200 }}
            component={
              <>
                <Timeline className="upcom-timeline">
                  {holidaysData?.length > 0 &&
                    holidaysData?.map((holidayData) => {
                      const isToday = getIsToday(holidayData?.date);
                      return (
                        <Timeline.Item key={holidayData?.date}>
                          <div className="upcom-timeline-flex">
                            <div className="upcom-timeline-title">
                              <div
                                className={
                                  isToday
                                    ? 'upcom-timeline-time text-action'
                                    : 'upcom-timeline-time'
                                }
                              >
                                {holidayData?.date}
                              </div>
                              <span
                                className={
                                  isToday
                                    ? 'upcom-timeline-week text-action'
                                    : 'upcom-timeline-week'
                                }
                              >
                                {getWeekText(holidayData?.date)}
                              </span>
                              {isToday ? (
                                <span className="upcom-timeline-week text-action">
                                  {formatMessage({ id: 'dbh_today' })}
                                </span>
                              ) : (
                                ''
                              )}
                            </div>
                            <div className="upcom-timeline-countries">
                              {holidayData?.holidays?.map((holiday, i) => (
                                <div key={i} className="upcom-timeline-desc">
                                  <Bubble
                                    dark
                                    content={getLocalText(
                                      locale,
                                      holiday,
                                      'name',
                                      'name_cn',
                                    )}
                                  >
                                    <div className="upcom-timeline-text">
                                      {getLocalText(
                                        locale,
                                        holiday,
                                        'name',
                                        'name_cn',
                                      )}
                                    </div>
                                  </Bubble>
                                  <PopoverContext data={holiday} />
                                </div>
                              ))}
                            </div>
                          </div>
                        </Timeline.Item>
                      );
                    })}
                </Timeline>
              </>
            }
          />
        </Card.Body>
      </Card>
      {calendarVisible && (
        <DateSchedule
          regionType={regionType}
          regionValue={transGlobalValue(regionValue)}
          setVisible={setCalendarVisible}
        />
      )}
    </>
  );
}

const PopoverContext: React.FC<IPopoverDateProps> = memo((props) => {
  const { data } = props;
  const [visible, setVisible] = useState(false);

  return (
    <Popover
      visible={visible}
      onVisibleChange={(visible) => setVisible(visible)}
      placement="right"
      trigger="hover"
      overlay={<PopoverDate data={data} />}
    >
      <div className="upcom-timeline-num">{data?.country_size}</div>
    </Popover>
  );
});
